import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Row from '../../layout/Row';
import { servicesConfig } from '../../../pages/Services/servicesConfig';

const SwitcherContainer = styled.button`
  border: 0;
  background: transparent;
  color: var(--ink);
  font-size: 1.3rem;
  min-width: 44px;
  min-height: 44px;
  padding: 0 6px;
  display: flex;
  align-items: center;
  > div {
    margin: 0;
    opacity: 1 !important;
  }
  i {
    display: none;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 5px 0;
  width: fit-content;
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.isOpen ? '1' : '0')};
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
  z-index: 10; // Ensure dropdown is above other content
`;

const LanguageOption = styled.div`
  cursor: pointer;
  color: #333;
  padding: 10px;
  margin-left: auto;
  width: 100%;
  text-align: center;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const I = styled.i`
  font-size: 1.7rem;
  color: #c5c5c5;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-left: 0.13rem;
  }
`;

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const languages = [
    { code: 'en', name: 'EN' },
    { code: 'fr', name: 'FR' },
    // Add more languages as needed
  ];

  const supportedLanguages = ['en', 'fr'];

  const basePathMap: Record<'en' | 'fr', Record<string, string>> = {
    en: {
      'a-propos': 'about',
      'nos-realisations': 'our-work',
      'politique-de-confidentialite': 'privacy-policy',
      'termes-et-conditions': 'terms-and-conditions',
      'mentions-legales-et-cookies': 'legal-notice-cookies',
      'nos-services': 'services',
      'our-services': 'services',
    },
    fr: {
      about: 'a-propos',
      'our-work': 'nos-realisations',
      'privacy-policy': 'politique-de-confidentialite',
      'terms-and-conditions': 'termes-et-conditions',
      'legal-notice-cookies': 'mentions-legales-et-cookies',
      'our-services': 'services',
      'nos-services': 'services',
    },
  };

  const mapServiceSlug = (slug: string, targetLang: 'en' | 'fr') => {
    const service = servicesConfig.find(
      (entry) => entry.slugs.en === slug || entry.slugs.fr === slug,
    );
    return service ? service.slugs[targetLang] : slug;
  };

  const mapProjectPath = (
    slug: string,
    currentLang: 'en' | 'fr',
    targetLang: 'en' | 'fr',
  ) => {
    const fromBundle = i18n.getResourceBundle(currentLang, 'ourWork');
    const toBundle = i18n.getResourceBundle(targetLang, 'ourWork');
    const fromProjects = fromBundle?.projects || {};
    const toProjects = toBundle?.projects || {};
    const match = Object.entries(fromProjects).find(
      ([, project]) => (project as { slug?: string })?.slug === slug,
    );
    if (!match) return null;
    const [key] = match;
    const targetProject = toProjects[key] as
      | { internalLink?: string }
      | undefined;
    return targetProject?.internalLink || null;
  };

  // Function to get a readable name for the current language
  const getCurrentLanguageName = () => {
    const currentLangCode = i18n.language.split('-')[0];
    const currentLanguage = languages.find(
      (lang) => lang.code !== currentLangCode,
    );
    return currentLanguage ? currentLanguage.name : '';
  };

  // const switchLanguage = (lng) => {
  //   i18n.changeLanguage(lng)
  //   setIsOpen(false) // Close the dropdown after switching
  // }

  const switchLanguage = (lng) => {
    i18n.changeLanguage(lng).then(() => {
      const targetLang = lng as 'en' | 'fr';
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const currentLang = supportedLanguages.includes(pathSegments[0])
        ? (pathSegments.shift() as 'en' | 'fr')
        : (i18n.language.split('-')[0] as 'en' | 'fr');

      if (pathSegments.length === 0) {
        const homePath = targetLang === 'fr' ? '/fr' : '/';
        navigate(homePath, { replace: true });
        return;
      }

      const [baseSegment, ...rest] = pathSegments;
      const mappedBase = basePathMap[targetLang][baseSegment] || baseSegment;

      if (
        (baseSegment === 'our-work' || baseSegment === 'nos-realisations') &&
        rest.length > 0
      ) {
        const mappedProject = mapProjectPath(rest[0], currentLang, targetLang);
        if (mappedProject) {
          navigate(mappedProject, { replace: true });
          return;
        }
      }

      if (baseSegment === 'services' && rest.length > 0) {
        const mappedSlug = mapServiceSlug(rest[0], targetLang);
        navigate(`/${targetLang}/${mappedBase}/${mappedSlug}`, {
          replace: true,
        });
        return;
      }

      if (baseSegment === 'login') {
        navigate(`/${targetLang}/app/login`, { replace: true });
        return;
      }

      const newPath = `/${targetLang}/${[mappedBase, ...rest].join('/')}`;
      const normalizedPath =
        targetLang === 'en' && newPath === '/en' ? '/' : newPath;
      navigate(normalizedPath, { replace: true });
    });
  };

  // Determine the current language
  const currentLangCode = i18n.language.split('-')[0];
  // Sort languages so that the non-current language comes first
  const sortedLanguages = languages.filter((a) => a.code !== currentLangCode);

  return (
    <SwitcherContainer
      type="button"
      aria-label={
        currentLangCode === 'en' ? 'Passer en français' : 'Switch to English'
      }
      onClick={() => switchLanguage(currentLangCode === 'en' ? 'fr' : 'en')}
    >
      <Row
        align="center"
        justify="center"
        margin="1rem 0"
        style={{
          opacity: '70%',
        }}
      >
        <I className="fa-solid fa-globe"></I> &nbsp; {getCurrentLanguageName()}
      </Row>
      {/* {isOpen && (
        <Dropdown isOpen={isOpen}>
          {sortedLanguages
            // .filter((lang) => lang.code !== i18n.language.split('-')[0]) // Filter out the current language
            .map((lang) => (
              <LanguageOption key={lang.code} onClick={() => switchLanguage(lang.code)}>
                {lang.name}
              </LanguageOption>
            ))}
        </Dropdown>
      )} */}
    </SwitcherContainer>
  );
};

export default LanguageSwitcher;
